import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, getDoc, doc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { FIREBASE_CONFIG } from '../config/constants';

function PastInterviews() {
  const { user } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return null;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          return userDoc.data();
        }
        return null;
      } catch (err) {
        console.error('Error fetching user data:', err);
        return null;
      }
    };
    
    const fetchInterviews = async () => {
      if (!user) {
        console.log('No user found, skipping fetch');
        setLoading(false);
        return;
      }

      try {
        console.log('Fetching interviews for user:', user.uid);
        
        // Fetch user data
        const userData = await fetchUserData();
        setUserData(userData);
        
        // Query for interviews with feedback
        const interviewsQuery = query(
          collection(db, FIREBASE_CONFIG.INTERVIEW_COLLECTION()),
          where('uid', '==', user.uid),
          where('has_feedback', '==', true),  // Only show interviews with feedback
          orderBy('timestamp', 'desc')  // Most recent interviews first
        );
        
        const querySnapshot = await getDocs(interviewsQuery);
        const interviews = [];
        
        querySnapshot.forEach((doc) => {
          console.log('Document data:', doc.data());
          const data = doc.data();
          interviews.push({
            id: doc.id,
            timestamp: data.timestamp?.toDate(),
            session: data.session || [],
            updated_at: data.updated_at?.toDate()
          });
        });
        
        setInterviews(interviews);
        
        // Update user counter display
        if (userData) {
          const actualCompleted = querySnapshot.size;
          const actualRemaining = Math.max(0, userData.interview_quota - actualCompleted);
          
          console.log('DEBUG: Past Interviews - Actual counts:', {
            queryCount: actualCompleted,
            quota: userData.interview_quota,
            storedCompleted: userData.total_interviews_completed || 0,
            storedRemaining: userData.interviews_remaining || 0,
            actualRemaining: actualRemaining
          });
          
          // For display purposes only - make a temporary userData object with accurate counts
          setUserData({
            ...userData,
            _actualInterviewsWithFeedback: actualCompleted,
            _actualInterviewsRemaining: actualRemaining
          });
        }
      } catch (err) {
        console.error('Error fetching interviews:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchInterviews();
  }, [user]);

  const formatDate = (date) => {
    if (!date) return 'No date';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0B0F17] text-gray-100 py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-500/10 text-red-400 p-4 rounded-lg">
            Error: {error}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0F17] text-gray-100 py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Recent Interviews
            </h1>
            {userData && (
              <div className="mt-2 text-gray-300">
                <span className="mr-4">
                  <span className="text-blue-400 font-semibold">
                    {userData._actualInterviewsWithFeedback !== undefined 
                      ? userData._actualInterviewsWithFeedback 
                      : (userData.total_interviews_completed || 0)}
                  </span> Completed
                </span>
                <span>
                  <span className="text-blue-400 font-semibold">
                    {userData._actualInterviewsRemaining !== undefined
                      ? userData._actualInterviewsRemaining
                      : (userData.interviews_remaining || 0)}
                  </span> Remaining
                </span>
                {userData.has_purchased_premium && (
                  <span className="ml-4 text-xs bg-green-900/30 px-2 py-1 rounded">
                    Premium Active
                  </span>
                )}
              </div>
            )}
          </div>
          <Link
            to="/interview"
            className="mt-4 md:mt-0 px-4 py-2 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors"
          >
            Begin Interview
          </Link>
        </div>

        {interviews.length === 0 ? (
          <div className="bg-gray-900/30 backdrop-blur-sm rounded-2xl p-8 border border-gray-800/50 text-center">
            <h2 className="text-xl font-semibold text-gray-200 mb-4">No Finished Interviews Yet</h2>
            <p className="text-gray-400 mb-6">Start your first interview to begin tracking your progress.</p>
            <Link
              to="/interview"
              className="px-6 py-3 bg-purple-500/10 text-purple-400 rounded-lg hover:bg-purple-500/20 transition-colors inline-block"
            >
              Begin Interview
            </Link>
          </div>
        ) : (
          <div className="grid gap-6">
            {interviews.map((interview) => (
              <div 
                key={interview.id}
                className="bg-gray-900/30 backdrop-blur-sm rounded-2xl p-6 border border-gray-800/50"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <div className="text-sm text-gray-400">
                      {formatDate(interview.timestamp)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Last updated: {formatDate(interview.updated_at)}
                    </div>
                  </div>
                  <Link
                    to={`/feedback/${interview.id}`}
                    className="px-4 py-2 bg-emerald-500/10 text-emerald-400 rounded-lg hover:bg-emerald-500/20 transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default PastInterviews;